import { AppDate } from '../envTypes';

import { ModelBase } from './base';
import { Profile } from './profile';
import { StoreFront } from './store';
import { UpcomingShow } from './upcoming-show';
import type { LiveStream, GiveawayItem } from './livestream';
import type { Listing } from './listing';
import type { UserBadge } from './badge';
import type { ShowEvent, ShowTicket } from './event';
import type { OrderVideoMessage } from './order-review';
import type { PromotionPage } from './misc';
import { ReferralGift } from './referral-gifting';
import { Referral } from './referral';

export interface ContentFDL {
  // link is the one hitting our website. by providing the compaign_id,
  // our backend receive the id and do following things.
  link?: string; // https://popshop.live/JapanLA?c=campaign_id;
  android?: {
    apn?: string; // The package name of the Android app to use to open the link. The app must be connected to your project from the Overview page of the Firebase console. Required for the Dynamic Link to open an Android app.
    afl?: string; // The link to open when the app isn't installed. Specify this to do something other than install your app from the Play Store when the app isn't installed, such as open the mobile web version of the content, or display a promotional page for your app.
    amv?: string; // The versionCode of the minimum version of your app that can open the link. If the installed app is an older version, the user is taken to the Play Store to upgrade the app.
  };
  ios?: {
    ibi?: string; //	The bundle ID of the iOS app to use to open the link. The app must be connected to your project from the Overview page of the Firebase console. Required for the Dynamic Link to open an iOS app.
    ifl?: string; //	The link to open when the app isn't installed. Specify this to do something other than install your app from the App Store when the app isn't installed, such as open the mobile web version of the content, or display a promotional page for your app.
    ius?: string; //	Your app's custom URL scheme, if defined to be something other than your app's bundle ID
    ipfl?: string; //	The link to open on iPads when the app isn't installed. Specify this to do something other than install your app from the App Store when the app isn't installed, such as open the web version of the content, or display a promotional page for your app.
    ipbi?: string; //	The bundle ID of the iOS app to use on iPads to open the link. The app must be connected to your project from the Overview page of the Firebase console.
    isi?: string; //	Your app's App Store ID, used to send users to the App Store when the app isn't installed
    imv?: string; //	The version number of the minimum version of your app that can open the link. This flag is passed to your app when it is opened, and your app must decide what to do with it.
    efr?: string; //	If set to '1', skip the app preview page when the Dynamic Link is opened, and instead redirect to the app or store. The app preview page (enabled by default) can more reliably send users to the most appropriate destination when they open Dynamic Links in apps; however, if you expect a Dynamic Link to be opened only in apps that can open Dynamic Links reliably without this page, you can disable it with this parameter. This parameter will affect the behavior of the Dynamic Link only on iOS.
  };
  other?: {
    ofl?: string; //	The link to open on platforms beside Android and iOS. This is useful to specify a different behavior on desktop, like displaying a full web page of the app content/payload (as specified by param link) with another dynamic link to install the app.
  };
  social?: {
    st?: string; //	The title to use when the Dynamic Link is shared in a social post.
    sd?: string; //	The description to use when the Dynamic Link is shared in a social post.
    si?: string; //	The URL to an image related to this link. The image should be at least 300x200 px, and less than 300 KB.
  };
  analytics?: {
    googlePlay?: {
      utm_source?: string;
      utm_medium?: string;
      utm_campaign?: string;
      utm_term?: string;
      utm_content?: string;
      gclid?: string;
    };
    itunesConnect?: {
      at?: string;
      ct?: string;
      mt?: string;
      pt?: string;
    };
  };
}

export enum ShareTargetType {
  Profile = 'PROFILE',
  Store = 'STORE',
  LiveStream = 'LIVE_STREAM',
  Replay = 'REPLAY',
  UpcomingShow = 'UPCOMING_SHOW',
  ListingLiveStream = 'LISTING_LIVE_STREAM',
  ListingReplay = 'LISTING_REPLAY',
  ListingEvergreen = 'LISTING_EVERGREEN',
  ListingUpcomingShow = 'LISTING_UPCOMING_SHOW',
  ListingPromotion = 'LISTING_PROMOTION',
  Event = 'EVENT',
  EventDiscovery = 'EVENT_DISCOVERY',
  EventTicket = 'EVENT_TICKET',
  HomePage = 'HOME_PAGE',
  Promotion = 'PROMOTION',
  ReferralGifting = 'REFERRAL_GIFTING',
  UserBadge = 'USER_BADGE',
  LiveStreamGiveaway = 'LIVE_STREAM_GIVEAWAY',
  UpcomingShowGiveaway = 'UPCOMING_SHOW_GIVEAWAY',
  PopClip = 'POP_CLIP',
  BecomeASeller = 'BECOME_A_SELLER',
  OrderReview = 'ORDER_REVIEW',
}

export type ShareTarget =
  | {
      type: ShareTargetType.UserBadge;
      userId: string;
      userBadgeId: string;
      userBadge: UserBadge;
    }
  | {
      type: ShareTargetType.Profile;
      profileId: string;
      profile: Profile;
    }
  | {
      type: ShareTargetType.Store;
      storeId: string;
      store: StoreFront;
    }
  | {
      type: ShareTargetType.LiveStream;
      liveStreamId: string;
      liveStream: LiveStream;
    }
  | {
      type: ShareTargetType.Replay;
      liveStreamId: string;
      liveStream: LiveStream;
    }
  | {
      type: ShareTargetType.UpcomingShow;
      upcomingShowId: string;
      upcomingShow: UpcomingShow;
    }
  | {
      type: ShareTargetType.ListingLiveStream;
      listingId: string;
      liveStreamId: string;
      listing: Listing;
      liveStream: LiveStream;
    }
  | {
      type: ShareTargetType.ListingReplay;
      listingId: string;
      liveStreamId: string;
      listing: Listing;
      liveStream: LiveStream;
    }
  | {
      type: ShareTargetType.ListingEvergreen;
      listingId: string;
      storeId: string;
      store: StoreFront;
      listing: Listing;
    }
  | {
      type: ShareTargetType.Event | ShareTargetType.EventDiscovery;
      showEventId: string;
      showEvent: ShowEvent;
    }
  | {
      type: ShareTargetType.EventTicket;
      showEventId: string;
      ticketId: string;
      ticket: ShowTicket;
      showEvent: ShowEvent;
    }
  | {
      type: ShareTargetType.HomePage;
    }
  | {
      type: ShareTargetType.Promotion;
      promotionId: string;
      promotion: PromotionPage;
    }
  | {
      type: ShareTargetType.ListingPromotion;
      promotionId: string;
      promotion: PromotionPage;
      storeId: string;
      store: StoreFront;
      listingId: string;
      listing: Listing;
    }
  | {
      type: ShareTargetType.ListingUpcomingShow;
      upcomingShowId: string;
      upcomingShow: UpcomingShow;
      storeId: string;
      store: StoreFront;
      listingId: string;
      listing: Listing;
    }
  | {
      type: ShareTargetType.ReferralGifting;
      referralGiftId: string;
      referralGift: ReferralGift;
      variantId?: string;
    }
  | {
      type: ShareTargetType.LiveStreamGiveaway;
      liveStreamId: string;
      liveStream: LiveStream;
      giveawayItemId: string;
      giveawayItem: GiveawayItem;
    }
  | {
      type: ShareTargetType.UpcomingShowGiveaway;
      upcomingShowId: string;
      upcomingShow: UpcomingShow;
      giveawayItemId: string;
      giveawayItem: GiveawayItem;
    }
  | {
      type: ShareTargetType.PopClip;
      storeId: string;
      // a numeric id from DynamoDB (might be switched to firestore in the future)
      popClipId: string;
      store: StoreFront;
    }
  | {
      type: ShareTargetType.BecomeASeller;
    }
  | {
      type: ShareTargetType.OrderReview;
      orderReviewId: string;
      orderReview: OrderVideoMessage;
    };

export enum ShareMethod {
  Sms = 'SMS',
  Email = 'EMAIL',
  Facebook = 'FACEBOOK',
  Instagram = 'INSTAGRAM',
  Twitter = 'TWITTER',
  Embed = 'EMBED',
  Generic = 'GENERIC',
}

export enum LinkType {
  Share = 'SHARE',
  Campaign = 'CAMPAIGN',
  ReferralGifting = 'REFERRAL_GIFTING',
}

export type FDLComponent = {
  shortLinkFDL: string;
  contentFDL: ContentFDL;
  // params for the link [contentFDL.link], which is denormalized for future data query.
  queryParams?: Record<string, string>;
};

export type BehavioralActions = {
  actions?: {
    usersToFollow?: string[];
    joinToUserGroups?: string[];
    addToWatchLists?: string[];
  };
};

export type LinkBase = FDLComponent &
  ModelBase &
  BehavioralActions & {
    shareTarget: ShareTarget;
    isActive: boolean;
  };

export type Share = {
  type: LinkType.Share;
  userId: string; // user id of who shares
  profile: Profile; // user profile of who shares
  method: ShareMethod;
} & LinkBase;

export type ReferralGiftingLink = {
  type: LinkType.ReferralGifting;
  userId: string; // user id of who shares
  profile: Profile; // user profile of who shares
  referralGiftId: string;
  referralGift: ReferralGift;
  sentSuccess: boolean;
  variantId?: string;
} & LinkBase;

// https://firebase.google.com/docs/dynamic-links/create-manually?authuser=0
// /campaigns/{campaign_id}/
export type Campaign = {
  type: LinkType.Campaign;
  name?: string;
  isWebOnly?: boolean;
  // https://popshoplive.page.link/JapanLA
  // the target user this campaign is attributed to
} & LinkBase;

export type Link = Share | Campaign | ReferralGiftingLink;

export type LinkHistory = {
  linkId: string;
  link: Link;
  userId: string;
  profile: Profile;
  isNewUser: boolean;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  extraData?: Record<string, any>;
  userAgent?: string;
} & ModelBase;

// output from bigquery
// user stats
// /userStats/{userId}
export type ExposureUserStats = {
  updatedAt?: AppDate;
  followerCount?: number;
  followingCount?: number;
  passcodeReferralCount?: number;
  shareReferralCount?: number;
  /**
   * the number of users I invited
   */
  referralCount?: number;
  /**
   * the user who invited me
   */
  referrerUserId?: string;
  referrerUserProfile?: Profile;
  /**
   * @deprecated since 03/15/2024, migrated to PrivateProfile
   */
  referrerManuallyModifiedAt?: AppDate;
};

// user referral(i.e. signup) from a user's passcode and share
// /userStats/{referrerUserId}/userReferrals/{presenteeUserId}
export type ExposureUserReferral = {
  createdAt: AppDate;
  updatedAt: AppDate;
  type: 'SHARE' | 'PASSCODE' | 'MANUAL';
  /**
   * the invitee
   */
  presenteeUserId: string;
  presenteeUserProfile: Profile;
  /**
   * the user who invited the invitee
   */
  referrerUserId: string;
  referrerUserProfile: Profile;
  passcode: Referral | null;
  share: LinkHistory | null;
};

// show referral(i.e. signup and join a show) from a user's share
// /sellerStats/{sellerId}/showStats/{liveStreamId}/showReferrals/{presenteeUserId}
export type ExposureShowReferral = {
  createdAt: AppDate;
  updatedAt: AppDate;
  presenteeUserId: string;
  presenteeUserProfile: Profile;
  referrerUserId: string;
  referrerUserProfile: Profile;
  share: LinkHistory;
};

// upcoming show referral(i.e. signup and watch a upcoming show) from a user's share
// /sellerStats/{sellerId}/upcomingShowStats/{upcomingShowId}/upcomingShowReferrals/{presenteeUserId}
export type ExposureUpcomingShowReferral = ExposureShowReferral;
